import React, { useState, useEffect, useRef } from 'react';
import { useChat } from '../contexts/ChatContext';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { useLiveSupport } from '../contexts/LiveSupportContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle,
  X,
  Send,
  Users,
  WifiOff,
  Minimize2,
  Shield,
  Trash2,
  Headphones,
  MessageSquare,
  ArrowLeft,
  CloudRain,
  Gift
} from 'lucide-react';
import './ChatWidget.css';
import ShinyText from './ShinyText';
import LiveSupportModal from './LiveSupportModal';
import LiveSupportChat from './LiveSupportChat';

const ChatWidget: React.FC = () => {
  const { currentPlayer } = useSarLabAuth();
  const {
    messages,
    onlineUsers,
    isConnected,
    unreadCount,
    sendMessage,
    deleteMessage,
    setTyping,
    typingUsers,
    markAsRead,
    isBanned,
    isTimedOut,
    timeoutUntil,
    activeRain,
    rainCountdown,
    isParticipatingInRain,
    showRainNotification,
    dismissRainNotification
  } = useChat();
  const { activeTicket } = useLiveSupport();

  const [isOpen, setIsOpen] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [activeView, setActiveView] = useState<'menu' | 'chat' | 'support-form' | 'support-chat'>('menu');
  const [isMinimized, setIsMinimized] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [isTyping, setIsTypingState] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Aktif ticket varsa support chat'e yönlendir ve widget'ı aç
  useEffect(() => {
    if (activeTicket) {
      setActiveView('support-chat');
      // Sayfa yenilendiğinde aktif ticket varsa widget'ı otomatik aç
      if (!isOpen && !showMenu) {
        setIsOpen(true);
      }
    }
  }, [activeTicket, isOpen, showMenu]);

  // Mesajları otomatik scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Typing timeout
  useEffect(() => {
    if (isTyping) {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        setIsTypingState(false);
        setTyping(false);
      }, 3000);
    }
  }, [isTyping, setTyping]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageText.trim() || !currentPlayer) return;

    if (isBanned) {
      alert('Hesabınız banlanmıştır. Mesaj gönderemezsiniz.');
      return;
    }

    if (isTimedOut) {
      const remainingTime = timeoutUntil ? Math.ceil((timeoutUntil - Date.now()) / 60000) : 0;
      alert(`Hesabınız timeout edilmiştir. ${remainingTime} dakika sonra mesaj gönderebilirsiniz.`);
      return;
    }

    await sendMessage(messageText);
    setMessageText('');
    setIsTypingState(false);
    setTyping(false);
  };

  // Chat açıldığında mesajları okundu olarak işaretle
  useEffect(() => {
    if (isOpen && activeView === 'chat') {
      markAsRead();
    }
  }, [isOpen, activeView, markAsRead]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setMessageText(value);

    if (value.trim() && !isTyping) {
      setIsTypingState(true);
      setTyping(true);
    } else if (!value.trim() && isTyping) {
      setIsTypingState(false);
      setTyping(false);
    }
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('tr-TR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTypingText = () => {
    if (typingUsers.length === 0) return '';
    if (typingUsers.length === 1) {
      const user = onlineUsers.find(u => u.odvisitId === typingUsers[0]);
      return `${user?.odvisitUsername || 'Birisi'} yazıyor...`;
    }
    return `${typingUsers.length} kişi yazıyor...`;
  };

  const handleButtonClick = () => {
    if (isOpen) {
      setIsOpen(false);
      setShowMenu(false);
    } else {
      setShowMenu(true);
    }
  };

  const handleMenuSelect = (view: 'chat' | 'support-form') => {
    setShowMenu(false);
    setIsOpen(true);
    setActiveView(activeTicket ? 'support-chat' : view);
  };

  const handleBack = () => {
    if (activeTicket) {
      setActiveView('support-chat');
    } else {
      setIsOpen(false);
      setShowMenu(true);
    }
  };

  const handleSupportFormSuccess = () => {
    setActiveView('support-chat');
  };

  if (!currentPlayer) return null;

  // Chat bağlantı durumu kontrolü
  if (!isConnected) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        className="fixed bottom-6 right-6 z-20 w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20"
        title="Chat bağlantısı yok"
      >
        <div className="flex items-center justify-center">
          <WifiOff className="w-6 h-6 text-white" />
        </div>
      </motion.button>
    );
  }

  return (
    <>
      {/* Rain Notification Popup - Shows when chat is closed */}
      <AnimatePresence>
        {showRainNotification && activeRain && rainCountdown > 0 && !isOpen && (
          <motion.div
            initial={{ opacity: 0, x: 100, scale: 0.8 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.8 }}
            className="fixed bottom-24 right-6 z-30 bg-gradient-to-r from-purple-900/95 to-pink-900/95 backdrop-blur-md border border-purple-500/30 rounded-xl shadow-2xl p-4 w-72 sm:bottom-24 bottom-36"
          >
            <button
              onClick={dismissRainNotification}
              className="absolute top-2 right-2 p-1 hover:bg-white/10 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>

            <div className="flex items-center space-x-3 mb-3">
              <div className="relative">
                <CloudRain className="w-8 h-8 text-purple-400 animate-bounce" />
                <Gift className="w-4 h-4 text-yellow-400 absolute -bottom-1 -right-1" />
              </div>
              <div>
                <div className="text-lg font-bold text-white">Rain Yagmuru!</div>
                <div className="text-sm text-purple-300">
                  {parseFloat(activeRain.totalAmount).toLocaleString('tr-TR')} TL
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between bg-black/20 rounded-lg p-3">
              <div>
                <div className="text-xs text-purple-300/70">Kalan Sure</div>
                <div className="text-3xl font-bold text-white tabular-nums">{rainCountdown}s</div>
              </div>
              <button
                onClick={() => {
                  setShowMenu(false);
                  setIsOpen(true);
                  setActiveView('chat');
                  dismissRainNotification();
                }}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white text-sm font-semibold rounded-lg transition-all transform hover:scale-105"
              >
                Katil!
              </button>
            </div>

            <div className="mt-2 text-xs text-center text-purple-300/60">
              Mesaj at ve kazanma sansı yakala!
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Chat Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={handleButtonClick}
        className={`fixed bottom-6 right-6 z-20 w-14 h-14 rounded-full shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20 ${
          isConnected
            ? 'bg-primary-500 hover:bg-primary-600'
            : 'bg-red-500 hover:bg-red-600'
        }`}
      >
        <div className="flex items-center justify-center">
          <MessageCircle className="w-6 h-6 text-white" />
        </div>

        {/* Unread Messages Badge */}
        {unreadCount > 0 && !isOpen && !showMenu && (
          <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center">
            {unreadCount}
          </div>
        )}

        {/* Active Support Badge */}
        {activeTicket && !isOpen && !showMenu && (
          <div className="absolute -top-1 -left-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
        )}
      </motion.button>

      {/* Floating Menu Buttons */}
      <AnimatePresence>
        {showMenu && !isOpen && (
          <>
            {/* Backdrop to close menu - must be first/below buttons */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowMenu(false)}
              className="fixed inset-0 z-[19]"
            />

            {/* Live Support Button */}
            <motion.button
              initial={{ scale: 0, y: 0 }}
              animate={{ scale: 1, y: -140 }}
              exit={{ scale: 0, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              onClick={() => handleMenuSelect('support-form')}
              className="fixed bottom-6 right-6 z-[21] w-14 h-14 rounded-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20 flex items-center justify-center group"
            >
              <Headphones className="w-6 h-6 text-white" />
              <span className="absolute right-16 bg-dark-800 text-white text-sm px-3 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Canlı Destek
              </span>
              {activeTicket && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full animate-pulse" />
              )}
            </motion.button>

            {/* Chat Button */}
            <motion.button
              initial={{ scale: 0, y: 0 }}
              animate={{ scale: 1, y: -70 }}
              exit={{ scale: 0, y: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25, delay: 0.05 }}
              onClick={() => handleMenuSelect('chat')}
              className="fixed bottom-6 right-6 z-[21] w-14 h-14 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 shadow-2xl transition-all duration-300 sm:bottom-6 bottom-20 flex items-center justify-center group"
            >
              <MessageSquare className="w-6 h-6 text-white" />
              <span className="absolute right-16 bg-dark-800 text-white text-sm px-3 py-1 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                Sohbet
              </span>
              {unreadCount > 0 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {unreadCount}
                </div>
              )}
            </motion.button>
          </>
        )}
      </AnimatePresence>

      {/* Chat/Support Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{ duration: 0.2 }}
            className={`fixed bottom-24 right-6 z-20 bg-dark-800/95 backdrop-blur-md border border-dark-700/50 rounded-2xl shadow-2xl flex flex-col ${
              isMinimized
                ? 'w-80 h-16'
                : 'w-96 h-[500px] sm:h-[500px] h-[400px] sm:w-96 w-[calc(100vw-3rem)] sm:right-6 right-3'
            }`}
          >
            {/* Support Form View - show when explicitly on form view OR no active ticket */}
            {activeView === 'support-form' && (
              <LiveSupportModal
                onClose={() => {
                  setIsOpen(false);
                  setShowMenu(false);
                }}
                onSuccess={handleSupportFormSuccess}
                onBack={handleBack}
              />
            )}

            {/* Support Chat View - only show when on chat view AND has active ticket */}
            {activeView === 'support-chat' && activeTicket && (
              <LiveSupportChat
                onClose={() => {
                  setIsOpen(false);
                  setShowMenu(false);
                }}
                onStartNewChat={() => {
                  setActiveView('support-form');
                }}
              />
            )}

            {/* Regular Chat View */}
            {activeView === 'chat' && !activeTicket && (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-dark-700/50">
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={handleBack}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <ArrowLeft className="w-4 h-4 text-gray-400" />
                    </button>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm font-medium text-white">Sohbet</span>
                    </div>
                    {onlineUsers.length > 0 && (
                      <div className="flex items-center space-x-1 text-xs text-gray-400">
                        <Users className="w-3 h-3" />
                        <span>{onlineUsers.length}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setIsMinimized(!isMinimized)}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <Minimize2 className="w-4 h-4 text-gray-400" />
                    </button>
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        setShowMenu(false);
                      }}
                      className="p-1 hover:bg-dark-700/50 rounded transition-colors duration-200"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                {/* Ban/Timeout Warning */}
                {(isBanned || isTimedOut) && !isMinimized && (
                  <div className={`px-4 py-2 text-sm ${
                    isBanned
                      ? 'bg-red-900/30 border-b border-red-700/50 text-red-300'
                      : 'bg-yellow-900/30 border-b border-yellow-700/50 text-yellow-300'
                  }`}>
                    {isBanned ? (
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4" />
                        <span>Hesabınız banlanmıştır. Mesaj gönderemezsiniz.</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <Shield className="w-4 h-4" />
                        <span>
                          Hesabınız timeout edilmiştir.
                          {timeoutUntil && (
                            <span className="ml-1">
                              {Math.ceil((timeoutUntil - Date.now()) / 60000)} dakika sonra mesaj gönderebilirsiniz.
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Rain Banner */}
                <AnimatePresence>
                  {activeRain && rainCountdown > 0 && !isMinimized && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="bg-gradient-to-r from-purple-600/30 to-pink-600/30 border-b border-purple-500/30 px-4 py-3"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className="relative">
                            <CloudRain className="w-6 h-6 text-purple-400 animate-bounce" />
                            <Gift className="w-3 h-3 text-yellow-400 absolute -bottom-1 -right-1" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-purple-200">
                              Rain Aktif!
                            </div>
                            <div className="text-xs text-purple-300/80">
                              {parseFloat(activeRain.totalAmount).toLocaleString('tr-TR')} TL - Max {activeRain.maxWinners} kazanan
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-3">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-white tabular-nums">
                              {rainCountdown}
                            </div>
                            <div className="text-[10px] text-purple-300/70 uppercase">saniye</div>
                          </div>
                          {isParticipatingInRain ? (
                            <div className="px-2 py-1 bg-green-500/20 border border-green-500/30 rounded text-xs text-green-300">
                              Katıldınız
                            </div>
                          ) : (
                            <div className="px-2 py-1 bg-yellow-500/20 border border-yellow-500/30 rounded text-xs text-yellow-300 animate-pulse">
                              Mesaj at!
                            </div>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {!isMinimized && (
                  <>
                    {/* Messages */}
                    <div
                      className="flex-1 overflow-y-auto p-4 space-y-3 scrollbar-hide max-h-[300px] sm:max-h-none"
                    >
                      {messages.length === 0 ? (
                        <div className="text-center text-gray-400 text-sm py-8">
                          Henüz mesaj yok. İlk mesajı siz gönderin!
                        </div>
                      ) : (
                        messages.map((message) => (
                          <div
                            key={message.id}
                            className={`flex ${
                              message.userId === currentPlayer?.id ? 'justify-end' : 'justify-start'
                            }`}
                          >
                            <div
                              className={`max-w-xs px-3 py-2 rounded-lg group ${
                                message.userId === currentPlayer?.id
                                  ? 'bg-primary-500 text-white'
                                  : 'bg-dark-700/50 text-gray-100'
                              }`}
                            >
                              <div className={`text-xs mb-1 flex items-center space-x-1 ${
                                message.userId === currentPlayer?.id ? 'text-black-200' : 'text-gray-400'
                              }`}>
                                {message.isAdmin && (
                                  <Shield className="w-4 h-4 text-Black-400 animate-pulse" />
                                )}
                                {message.isAdmin ? (
                                  <ShinyText
                                    text={message.username || 'Admin'}
                                    speed={7}
                                    variant="black-white"
                                    className="font-small text-s"
                                  />
                                ) : (
                                  <ShinyText
                                    text={message.username || 'Bilinmeyen Kullanıcı'}
                                    speed={5}
                                    className="font-small text-s"
                                  />
                                )}
                              </div>
                              <div className="text-sm">{message.text}</div>
                              <div className="flex items-center justify-between mt-1">
                                <div className="text-xs opacity-70">
                                  {formatTime(message.timestamp)}
                                </div>
                                {false && /* TODO: SarLab admin role check */ (
                                  <button
                                    onClick={() => deleteMessage(message.id)}
                                    className="opacity-0 group-hover:opacity-100 hover:bg-red-500/20 p-1 rounded transition-all duration-200"
                                    title="Mesajı Sil"
                                  >
                                    <Trash2 className="w-3 h-3 text-red-400 hover:text-red-300" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}

                      {getTypingText() && (
                        <div className="text-xs text-gray-400 italic">
                          {getTypingText()}
                        </div>
                      )}

                      <div ref={messagesEndRef} />
                    </div>

                    {/* Message Input */}
                    <form onSubmit={handleSendMessage} className="p-4 border-t border-dark-700/50 flex-shrink-0">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={messageText}
                          onChange={handleInputChange}
                          placeholder={isBanned ? "Hesabınız banlanmıştır" : isTimedOut ? "Hesabınız timeout edilmiştir" : "Mesajınızı yazın..."}
                          className={`flex-1 px-3 py-2 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm ${
                            isBanned || isTimedOut
                              ? 'bg-red-900/50 border-red-600/50 text-red-300'
                              : 'bg-dark-700/50 border-dark-600/50'
                          }`}
                          disabled={!isConnected || isBanned || isTimedOut}
                        />
                        <button
                          type="submit"
                          disabled={!messageText.trim() || !isConnected || isBanned || isTimedOut}
                          className="p-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-200"
                        >
                          <Send className="w-4 h-4" />
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatWidget;
